import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileSignature, Banknote, Check, X, CheckCircle2, UserMinus, Copy, Pencil } from "lucide-react";
import api, { API, authToken } from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { Modal } from "../../components/Modal";
import { StudentForm } from "../../components/StudentForm";
import { StageBadge, STAGES, STAGE_ORDER } from "../../components/StageBadge";
import { ExportMenu } from "../../components/ExportMenu";
import { formatDate, formatTime, currency, formatApiErrorDetail, formatPT, classDayTime } from "../../lib/format";

function Field({ label, value }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-stone mb-1">{label}</p>
      <p className="font-mono text-sm text-ink">{value}</p>
    </div>
  );
}

const PORTAL_LABELS = { approved: "Cast", pending: "Pending", waitlisted: "Waitlist", declined: "Declined", dropped: "Dropped" };
const JOIN_SORTS = [
  { v: "newest", label: "Newest first" },
  { v: "oldest", label: "Oldest first" },
  { v: "name", label: "Name A–Z" },
  { v: "stage", label: "Stage" },
];

function JoinSignupsSection({ classId, joinSigs, onChanged }) {
  const [stageFilter, setStageFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [stageSel, setStageSel] = useState({});
  const [confirming, setConfirming] = useState(null); // {row, next, message}

  const filtered = useMemo(() => {
    let out = joinSigs.filter((r) => (stageFilter === "all" ? true : r.stage === stageFilter));
    out = [...out];
    if (sort === "oldest") out.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sort === "name") out.sort((a, b) => (a.full_name || "").toLowerCase().localeCompare((b.full_name || "").toLowerCase()));
    else if (sort === "stage") out.sort((a, b) => (STAGE_ORDER[a.stage] || 9) - (STAGE_ORDER[b.stage] || 9));
    else out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return out;
  }, [joinSigs, stageFilter, sort]);

  const counts = useMemo(() => {
    const c = {};
    STAGES.forEach((s) => { c[s] = joinSigs.filter((r) => r.stage === s).length; });
    return c;
  }, [joinSigs]);
  const trackedCount = (counts["Registered"] || 0) + (counts["Paid in full"] || 0);

  const applyStage = async (row, next) => {
    try {
      await api.patch(`/admin/signups/${row.id}`, { stage: next });
      toast.success(`${row.full_name}: ${row.stage || "—"} → ${next}`);
      setStageSel((s) => ({ ...s, [row.id]: "" }));
      setConfirming(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const requestUpdate = (row) => {
    const next = stageSel[row.id];
    if (!next || next === row.stage) return;
    const backwards = STAGE_ORDER[next] && STAGE_ORDER[row.stage] && STAGE_ORDER[next] < STAGE_ORDER[row.stage];
    if (next === "Withdrawn" || row.stage === "Withdrawn") {
      setConfirming({ row, next, message: `Move ${row.full_name} ${next === "Withdrawn" ? "to" : "out of"} Withdrawn?` });
    } else if (backwards) {
      setConfirming({ row, next, message: `Move ${row.full_name} back to ${next}?` });
    } else {
      applyStage(row, next);
    }
  };

  const copyEmails = async () => {
    const seen = new Set();
    const out = [];
    filtered.forEach((r) => {
      const k = (r.email || "").toLowerCase();
      if (k && !seen.has(k)) { seen.add(k); out.push(r.email); }
    });
    await navigator.clipboard.writeText(out.join(", "));
    toast.success(`${out.length} email${out.length === 1 ? "" : "s"} copied — ready for BCC`);
  };

  const printCols = [
    { key: "full_name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "stage", label: "Stage" },
    { key: "signed_up_at", label: "Signed up", render: (r) => formatPT(r.signed_up_at) },
    { key: "registered_at", label: "Registered", render: (r) => formatPT(r.registered_at) },
  ];

  const summary = STAGES.map((s) => `${counts[s] || 0} ${s.toLowerCase()}`).join(" · ");

  return (
    <div data-testid="join-page-signups">
      <h2 className="font-serif text-2xl text-ink mb-1">Join-page signups</h2>
      <p className="font-mono text-xs text-stone" data-testid="join-signups-summary">{summary}</p>
      <p className="font-sans text-xs text-stone mb-4" data-testid="join-signups-tracked-note">
        {trackedCount} marked Registered or Paid in full — tracked here, separate from the seat count above.
      </p>

      <div className="space-y-3 no-print mb-4">
        <div className="chip-row" data-testid="join-stage-chips">
          <button onClick={() => setStageFilter("all")} className={`filter-chip px-3 py-1 text-xs font-sans border ${stageFilter === "all" ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`} data-testid="join-stage-filter-all">
            All ({joinSigs.length})
          </button>
          {STAGES.map((s) => (
            <button key={s} onClick={() => setStageFilter(s)} className={`filter-chip px-3 py-1 text-xs font-sans border ${stageFilter === s ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`} data-testid={`join-stage-filter-${s.replace(/ /g, "-").toLowerCase()}`}>
              {s} ({counts[s] || 0})
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <select className="field-input !py-1.5 text-sm w-full sm:w-48" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="join-signups-sort">
            {JOIN_SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
          <div className="flex items-center gap-3">
            <button onClick={copyEmails} className="btn-ghost text-sm" data-testid="join-copy-emails">
              <Copy size={14} /> Copy emails
            </button>
            <ExportMenu
              list="class-join-signups"
              params={{ class_id: classId, stage: stageFilter === "all" ? "" : stageFilter, sort }}
              title="Join-page signups"
              columns={printCols}
              rows={filtered}
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((r) => {
          const portal = r.portal_status ? PORTAL_LABELS[r.portal_status] || r.portal_status : null;
          return (
            <div key={r.id} className="paper-card p-4" data-testid={`join-signup-row-${r.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-serif text-lg text-ink leading-tight">{r.full_name}</p>
                    <StageBadge stage={r.stage} />
                  </div>
                  <p className="font-mono text-xs text-stone mt-1 break-all">
                    {r.email}{r.phone ? ` · ${r.phone}` : ""}
                  </p>
                  <p className="font-mono text-xs text-stone mt-0.5">
                    Signed up — {formatPT(r.signed_up_at)} · Registered — {formatPT(r.registered_at)}
                  </p>
                  {portal && (
                    <p className="font-sans text-xs text-ink mt-1" data-testid={`portal-match-${r.id}`}>
                      Also in the portal: <span className="font-semibold">{portal}</span>
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1 w-full sm:w-auto shrink-0">
                  <div className="flex items-center gap-2">
                    <select
                      className="field-input !py-1.5 text-xs flex-1 sm:w-40"
                      value={stageSel[r.id] || ""}
                      onChange={(e) => setStageSel((s) => ({ ...s, [r.id]: e.target.value }))}
                      disabled={!!portal}
                      data-testid={`stage-select-${r.id}`}
                    >
                      <option value="">Stage…</option>
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button
                      onClick={() => requestUpdate(r)}
                      disabled={!!portal || !stageSel[r.id] || stageSel[r.id] === r.stage}
                      className="btn-outline text-xs disabled:opacity-40"
                      data-testid={`stage-update-${r.id}`}
                    >
                      Update
                    </button>
                  </div>
                  {portal && <p className="font-sans text-[11px] text-stone">Seat managed in the Cast list above</p>}
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="font-sans text-sm text-stone">No join-page signups {stageFilter === "all" ? "for this class yet" : "in this stage"}.</p>}
      </div>

      <Modal
        open={!!confirming}
        onClose={() => setConfirming(null)}
        title="Confirm stage change"
        testid="stage-confirm-modal"
        footer={
          <>
            <button onClick={() => setConfirming(null)} className="btn-ghost text-sm" data-testid="stage-confirm-cancel">Cancel</button>
            <button onClick={() => applyStage(confirming.row, confirming.next)} className="btn-brass text-sm" data-testid="stage-confirm-ok">Confirm</button>
          </>
        }
      >
        <p className="font-sans text-sm text-ink/80">{confirming ? confirming.message : ""}</p>
      </Modal>
    </div>
  );
}

export default function ReefClassDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [joinSigs, setJoinSigs] = useState([]);
  const [students, setStudents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmDrop, setConfirmDrop] = useState(null); // {regId, name}
  const [editingStudent, setEditingStudent] = useState(null);

  const load = useCallback(async () => {
    const [r, j, s] = await Promise.all([
      api.get(`/classes/${id}`),
      api.get(`/admin/classes/${id}/join-signups`).catch(() => ({ data: [] })),
      api.get("/students").catch(() => ({ data: [] })),
    ]);
    setC(r.data);
    setJoinSigs(j.data);
    setStudents(s.data);
  }, [id]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const markTaught = async () => {
    setBusy(true);
    try {
      await api.put(`/classes/${id}`, { ...clsPayload(c), status: "taught" });
      toast.success("Marked as taught — ready to bill.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const clsPayload = (cls) => ({
    title: cls.title, date: cls.date, start_time: cls.start_time, end_time: cls.end_time,
    venue: cls.venue, track: cls.track, capacity: cls.capacity, rate_per_student: cls.rate_per_student,
    status: cls.status, program: cls.program,
  });

  const billClass = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/classes/${id}/bill`);
      toast.success(r.data.created > 0 ? `Generated ${r.data.created} draft invoice(s).` : "No new invoices — all attendees already billed.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const act = async (regId, action) => {
    try {
      await api.post(`/registrations/${regId}/${action}`);
      toast.success(action === "approve" ? "Added to the cast." : "Declined.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const restoreReg = async (regId) => {
    try {
      const r = await api.post(`/registrations/${regId}/restore`);
      const reg = r.data.registration || {};
      let msg = reg.status === "approved"
        ? `Restored to the cast — seat #${reg.seat_number}.`
        : "Class is full — restored to the waitlist.";
      if (r.data.promotion_reverted && r.data.side_effect) msg += ` ${r.data.side_effect}.`;
      toast.success(msg);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doDrop = async (regId, name) => {
    setConfirmDrop(null);
    try {
      const r = await api.post(`/registrations/${regId}/drop`);
      const promoted = r.data.promoted;
      const msg = promoted
        ? `${name} dropped from the class. ${promoted.student_name} promoted to cast #${promoted.seat_number}`
        : `${name} dropped from the class.`;
      toast(msg, { duration: 12000, action: { label: "Undo", onClick: () => restoreReg(regId) } });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!c) return <p className="font-sans text-sm text-stone">Loading call sheet…</p>;

  const pending = (c.registrations || []).filter((r) => r.status === "pending");
  const waitlisted = (c.registrations || []).filter((r) => r.status === "waitlisted");
  const regByStudent = {};
  (c.registrations || []).forEach((r) => {
    if (r.status === "approved") regByStudent[r.student_id] = r.id;
  });
  const roster = c.roster || [];
  const dropped = (c.registrations || [])
    .filter((r) => r.status === "dropped")
    .sort((a, b) => (b.dropped_at || "").localeCompare(a.dropped_at || ""));
  const studentById = {};
  students.forEach((s) => { studentById[s.id] = s; });

  const begins = (() => {
    try {
      const d = new Date(String(c.date).slice(0, 10) + "T12:00:00");
      const wd = d.toLocaleDateString("en-US", { weekday: "long" });
      const md = d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
      return `Begins ${wd}, ${md} · ${classDayTime(c)} · ${c.venue}`;
    } catch {
      return "";
    }
  })();

  return (
    <div className="space-y-8" data-testid="reef-class-detail">
      <Link to="/reef/classes" className="btn-ghost text-sm no-print" data-testid="back-to-classes">
        <ArrowLeft size={15} /> All classes
      </Link>

      {/* Call sheet header */}
      <div className="paper-card p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="overline mb-2">Call Sheet · Day Sheet</p>
            <h1 className="font-serif text-4xl text-ink tracking-tight leading-none">{c.title}</h1>
            {begins && <p className="font-mono text-sm text-stone mt-2" data-testid="class-begins-line">{begins}</p>}
          </div>
          <StatusMark status={c.status} size={14} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 border-t border-hairline pt-6">
          <Field label="Date" value={formatDate(c.date)} />
          <Field label="Call" value={formatTime(c.start_time)} />
          <Field label="Wrap" value={formatTime(c.end_time)} />
          <Field label="Track" value={c.track} />
          <Field label="Rate" value={`${currency(c.rate_per_student)}/head`} />
          <Field label="Location" value={c.venue} />
        </div>
        <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-hairline font-mono text-sm" data-testid="class-counts-line">
          <span className="text-ink">{c.confirmed_count} <span className="text-stone">cast</span></span>
          <span className="text-ink">{c.open_seats} <span className="text-stone">open</span></span>
          <span className="text-ink">{c.pending_count} <span className="text-stone">pending</span></span>
          <span className="text-ink">{c.waitlist_count} <span className="text-stone">waitlist</span></span>
          <span className="text-ink">· {joinSigs.length} <span className="text-stone">join-page signups</span></span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 no-print">
        <button
          onClick={() => window.open(`${API}/classes/${id}/signin-sheet?auth=${authToken()}`, "_blank")}
          className="btn-brass text-sm"
          data-testid="generate-signin-sheet"
        >
          <FileSignature size={16} /> Sign-in sheet
        </button>
        {c.status === "scheduled" && (
          <button onClick={markTaught} disabled={busy} className="btn-outline text-sm" data-testid="mark-taught">
            <CheckCircle2 size={16} /> Mark taught
          </button>
        )}
        {c.track === "Independent" && (c.status === "taught" || c.status === "closed") && (
          <button onClick={billClass} disabled={busy} className="btn-outline text-sm" data-testid="bill-class">
            <Banknote size={16} /> Bill class
          </button>
        )}
        <Link to="/reef/sides" className="btn-ghost text-sm">Assign sides →</Link>
      </div>

      {/* Pending requests */}
      {pending.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl text-ink">Pending requests</h2>
            <ExportMenu
              list="class-pending"
              params={{ class_id: id }}
              title="Pending requests"
              columns={[
                { key: "student_name", label: "Name" },
                { key: "created_at", label: "Requested", render: (r) => formatPT(r.created_at) },
              ]}
              rows={pending}
            />
          </div>
          {pending.map((r) => (
            <div key={r.id} className="paper-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`detail-pending-${r.id}`}>
              <p className="font-serif text-lg text-ink">{r.student_name}</p>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setEditingStudent(studentById[r.student_id])} className="btn-ghost text-sm px-3 py-1.5" data-testid={`detail-edit-${r.student_id}`}>
                  <Pencil size={13} /> Edit
                </button>
                <button onClick={() => act(r.id, "approve")} className="btn-brass text-sm px-4 py-1.5" data-testid={`detail-approve-${r.id}`}>
                  <Check size={14} /> Approve
                </button>
                <button onClick={() => act(r.id, "decline")} className="btn-outline text-sm px-4 py-1.5 !border-garnet !text-garnet" data-testid={`detail-decline-${r.id}`}>
                  <X size={14} /> Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cast list */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-ink">Cast</h2>
          <ExportMenu
            list="class-cast"
            params={{ class_id: id }}
            title="Cast"
            columns={[
              { key: "seat_number", label: "#" },
              { key: "name", label: "Name", render: (r) => `${r.first_name} ${r.last_name}` },
              { key: "email", label: "Email" },
            ]}
            rows={roster}
          />
        </div>
        <div className="paper-card overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-hairline font-mono text-[10px] uppercase tracking-wider text-stone">
            <div className="col-span-1">#</div>
            <div className="col-span-3">Name</div>
            <div className="col-span-4">Email</div>
            <div className="col-span-4 text-right">Remove</div>
          </div>
          {roster.map((r) => (
            <div key={r.student_id} className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-hairline last:border-0 font-mono text-sm items-center" data-testid={`cast-${r.student_id}`}>
              <div className="col-span-1 text-stone">{r.seat_number}</div>
              <div className="col-span-3 text-ink">{r.first_name} {r.last_name}</div>
              <div className="col-span-4 text-stone truncate">{r.email || "—"}</div>
              <div className="col-span-4 flex flex-wrap justify-end items-center gap-2">
                <button
                  onClick={() => setEditingStudent(studentById[r.student_id])}
                  className="btn-ghost text-xs"
                  data-testid={`detail-edit-${r.student_id}`}
                >
                  <Pencil size={13} /> Edit
                </button>
                {regByStudent[r.student_id] && (
                  <button
                    onClick={() => setConfirmDrop({ regId: regByStudent[r.student_id], name: `${r.first_name} ${r.last_name}` })}
                    className="btn-outline text-xs !border-garnet !text-garnet"
                    data-testid={`drop-${r.student_id}`}
                  >
                    <UserMinus size={13} /> Drop from class
                  </button>
                )}
              </div>
            </div>
          ))}
          {roster.length === 0 && <p className="font-sans text-sm text-stone px-5 py-4">No confirmed cast yet.</p>}
        </div>
      </div>

      {/* Waitlist */}
      {waitlisted.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-serif text-2xl text-ink">Waitlist</h2>
            <ExportMenu
              list="class-waitlist"
              params={{ class_id: id }}
              title="Waitlist"
              columns={[
                { key: "student_name", label: "Name" },
                { key: "created_at", label: "Since", render: (r) => formatPT(r.created_at) },
              ]}
              rows={waitlisted}
            />
          </div>
          <p className="font-sans text-xs text-stone mb-4">First in line is offered the next seat automatically when an actor drops.</p>
          <div className="paper-card overflow-hidden">
            {waitlisted.map((r, i) => (
              <div key={r.id} className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-hairline last:border-0 font-mono text-sm items-center" data-testid={`waitlist-${r.id}`}>
                <div className="col-span-1 text-stone">{i + 1}</div>
                <div className="col-span-6 text-ink">{r.student_name}</div>
                <div className="col-span-5 flex flex-wrap justify-end items-center gap-2">
                  <button onClick={() => setEditingStudent(studentById[r.student_id])} className="btn-ghost text-xs" data-testid={`detail-edit-${r.student_id}`}>
                    <Pencil size={13} /> Edit
                  </button>
                  <StatusMark status="waitlisted" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dropped */}
      {dropped.length > 0 && (
        <div data-testid="dropped-section">
          <h2 className="font-serif text-2xl text-ink mb-1">Dropped</h2>
          <p className="font-sans text-xs text-stone mb-4">Restore puts an actor back in the cast — or on the waitlist if the class is full.</p>
          <div className="paper-card overflow-hidden">
            {dropped.map((r) => (
              <div key={r.id} className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-hairline last:border-0 font-mono text-sm items-center" data-testid={`dropped-${r.id}`}>
                <div className="col-span-3 text-ink">{r.student_name}</div>
                <div className="col-span-3 text-stone truncate">{(studentById[r.student_id] || {}).email || "—"}</div>
                <div className="col-span-3 text-stone text-xs">Dropped — {formatPT(r.dropped_at)}</div>
                <div className="col-span-3 flex justify-end">
                  <button onClick={() => restoreReg(r.id)} className="btn-outline text-xs" data-testid={`restore-${r.id}`}>
                    Restore to class
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Join-page signups */}
      <JoinSignupsSection classId={id} joinSigs={joinSigs} onChanged={load} />

      <Modal
        open={!!confirmDrop}
        onClose={() => setConfirmDrop(null)}
        title="Confirm drop"
        testid="drop-confirm-modal"
        footer={
          <>
            <button onClick={() => setConfirmDrop(null)} className="btn-ghost text-sm" data-testid="drop-confirm-cancel">Cancel</button>
            <button onClick={() => doDrop(confirmDrop.regId, confirmDrop.name)} className="btn-outline text-sm !border-garnet !text-garnet" data-testid="drop-confirm-ok">
              Drop from class
            </button>
          </>
        }
      >
        <p className="font-sans text-sm text-ink/80">
          {confirmDrop ? `Drop ${confirmDrop.name} from ${c.title}? They will move to the Dropped list and can be restored.` : ""}
        </p>
      </Modal>

      <StudentForm open={!!editingStudent} onClose={() => setEditingStudent(null)} initial={editingStudent} onSaved={load} />
    </div>
  );
}
